import React, { useState, useCallback, useEffect } from 'react';
import styled from 'styled-components';
import { paragon } from '@useparagon/connect';
import { useMergeLink } from '@mergeapi/react-merge-link';

import { usePortal } from '../../../providers';

import { H3, Icon, Text, Card, Button } from '../../../components';

import { SourceItem, AddSourceModal, CustomSourceModal } from '../components';

import { useIntegrations, useLinkToken, useSources } from '../providers';

const StyledH3 = styled(H3)`
  display: flex;
  column-gap: 4px;
  margin-bottom: 10px;
`;

const StyledCard = styled(Card)`
  row-gap: 40px;
  margin: 24px;
  width: 50%;
`;

const Main = styled.main`
  display: flex;
  flex-direction: column;
  row-gap: 20px;
`;

paragon.configureGlobal({ host: 'paragon.supper.co' });

const Sources = () => {
  const { sources, createSource, deleteSource } = useSources();
  const [selectedIntegration, setSelectedIntegration] = useState(null);
  const [linkToken, setLinkToken] = useState(null);
  const { integrations } = useIntegrations();
  const { createLinkToken, createParagonToken } = useLinkToken();
  const { showPortal } = usePortal();

  const addSourceWithParagon = useCallback((integration) => {
    paragon.connect(integration.name.replace('-paragon', ''), {
      onSuccess: (data) => {
        createSource(data.integrationId, integration.id, 'paragon');
      }
    });
  }, []);

  const addCustomSource = useCallback(
    (integration) => {
      showPortal(<CustomSourceModal integration={integration} handleCreateSource={createSource} />);
    },
    [showPortal]
  );

  useEffect(() => {
    createParagonToken();
  }, []);

  const handleIntegrationSelection = (integration, closeModal) => {
    closeModal();
    if (integration.name.endsWith('supper')) {
      return addCustomSource(integration);
    }
    if (integration.name.endsWith('paragon')) {
      return addSourceWithParagon(integration);
    }
    return setSelectedIntegration(integration);
  };

  useEffect(() => {
    if (selectedIntegration) {
      createLinkToken(selectedIntegration.name, selectedIntegration.schema).then(setLinkToken);
    }
  }, [selectedIntegration]);

  const { open, isReady } = useMergeLink({
    linkToken,
    onSuccess: (publicToken) => {
      createSource(publicToken);
      setSelectedIntegration(null);
      setLinkToken(null);
    },
    onExit: () => {
      setSelectedIntegration(null);
      setLinkToken(null);
    }
  });

  useEffect(() => {
    if (open && selectedIntegration && isReady) {
      open();
    }
  }, [open, selectedIntegration, isReady]);

  return (
    <StyledCard>
      <header>
        <StyledH3>Sources</StyledH3>
        <Text $color='var(--content-subtitle)'>
          This data is visible to your entire organization. Only admins can connect or disconnect
          new sources.
        </Text>
      </header>
      <Main>
        {sources?.map((source) => (
          <SourceItem
            key={source.id}
            id={source.id}
            name={source.integration.displayName}
            picture={source.integration.pictureUrl}
            lastSync={source.lastSync}
            onDelete={() => deleteSource(source.id)}
          />
        ))}
      </Main>

      <footer>
        <Button
          secondary
          onClick={() =>
            showPortal(
              <AddSourceModal
                integrations={integrations}
                handleIntegrationSelection={handleIntegrationSelection}
              />
            )
          }
          disabled={!isReady}
        >
          <Icon name='plus' size={24} />
          Connect new source
        </Button>
      </footer>
    </StyledCard>
  );
};

export { Sources };
